import type { LucideIcon } from 'lucide-react'
import {
  AlarmClockIcon,
  AudioLinesIcon,
  ClipboardCheckIcon,
  EyeIcon,
  LayersIcon,
  MusicIcon,
  SearchXIcon,
} from 'lucide-react'

export type Stat = {
  value: string
  label: string
  note?: string
}

export type IconItem = {
  icon: LucideIcon
  text: string
}

export type PackItem = {
  icon: LucideIcon
  title: string
  text: string
}

export type PageSample = {
  src: string
  level: string
  title: string
  caption: string
}

export type Bonus = {
  title: string
  text: string
  detail: string
  was: string
  cover: string
}

export type Faq = {
  q: string
  a: string
}

export const stats: Stat[] = [
  {
    value: '10',
    label: 'Kompletnych Tomów',
    note: 'Od podstaw po poziom mistrzowski',
  },
  { value: '500+', label: 'Stron Materiałów', note: 'Format PDF A4' },
  {
    value: '100%',
    label: 'Zgodne z Podstawą',
    note: 'Aktualne wymogi MEN',
  },
  {
    value: '100%',
    label: 'Gotowe do Druku',
    note: 'Oszczędność tuszu i czasu',
  },
]

export const differenceStats: Stat[] = [
  { value: '500+', label: 'Stron Treści' },
  { value: '4 → Matura', label: 'Od Klasy 4 do Matury' },
  { value: '100%', label: 'Praktycznych Rozwiązań' },
]

export const beforeItems: string[] = [
  'Godzinami układasz konspekty lekcji na każdy tydzień',
  'Przeszukujesz sieć w poszukiwaniu nagrań i nut',
  'Tracisz czas na ręczne rysowanie instrumentów i pięciolinii',
  'Stresujesz się przed hospitacją w swojej klasie',
  'Nie masz gotowych kluczy oceniania pod ręką',
]

export const afterItems: string[] = [
  'Kompletny plan na każdy dział muzyki',
  'Gotowe arkusze A4 z czystym zapisem nutowym i grafikami',
  'Jasna progresja od rytmu i dźwięku po analizę kultury',
  'Wskazówki dla nauczyciela i pomysły na aktywności krok po kroku',
  'Klucze oceniania i sprawdziany do każdego działu',
]

export const recognizeItems: IconItem[] = [
  {
    icon: AlarmClockIcon,
    text: 'Tracisz niedziele na przygotowywanie konspektów na poniedziałek',
  },
  {
    icon: SearchXIcon,
    text: 'Szukasz zadań, które są atrakcyjne wizualnie i wreszcie zaciekawią uczniów',
  },
  {
    icon: EyeIcon,
    text: 'Musisz szybko przygotować materiały, które wyróżnią Cię podczas hospitacji',
  },
]

export const packItems: PackItem[] = [
  {
    icon: LayersIcon,
    title: '10 Kompletnych Tomów',
    text: 'Od podstaw rytmu i dźwięku po zaawansowaną analizę kultury i maturę',
  },
  {
    icon: MusicIcon,
    title: 'Program 500+ stron',
    text: 'Uporządkowany tematycznie, w gotowym do druku formacie PDF A4',
  },
  {
    icon: ClipboardCheckIcon,
    title: 'Karty pracy, Sprawdziany i Odpowiedzi',
    text: 'Gotowe zestawy z pełnym kluczem oceniania',
  },
  {
    icon: AudioLinesIcon,
    title: 'Baza Instrumentów i Nuty',
    text: 'Profesjonalne grafiki i czyste zapisy nutowe',
  },
]

export const pageSamples: PageSample[] = [
  {
    src: 'https://cdn.magicpatterns.com/uploads/oq6qWPGmkyL3uTCrPBvExc/Captura-de-pantalla-2026-09-06-a-las-12.39.12_a._m..webp',
    level: 'Klasa 4 · Dźwięk i rytm',
    title: 'Ćwiczenie — Uzupełnij zdania',
    caption:
      'Zadania z wyborem odpowiedzi o dźwięku, rytmie i tempie, z ilustracją lekcji i oznaczeniem poziomu trudności.',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/3z7GkD63R7KQJ36zRW24nZ/Captura-de-pantalla-2026-09-06-a-las-12.40.02_a._m..webp',
    level: 'Instrumenty · Fortepian',
    title: 'Ćwiczenia — Fortepian',
    caption:
      'Etykiety klawiatury, praca lewej i prawej ręki oraz czysta pięciolinia do przepisania melodii.',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/3xgeWzQQFkvHPovVFPK9j9/Captura-de-pantalla-2026-09-06-a-las-12.40.32_a._m..webp',
    level: 'Folklor · Mazowsze',
    title: 'Strój i instrumenty mazowieckie',
    caption:
      'Trzy bloki ćwiczeń: składowe stroju ludowego, rozpoznawanie instrumentów i notacja mazowiecka.',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/9RXweMV7TpMmjHNphc2gQU/Captura-de-pantalla-2026-09-06-a-las-12.41.23_a._m..webp',
    level: 'Historia muzyki · Karty',
    title: 'Karty do wycięcia — ikony muzyki',
    caption:
      'Gotowe do wycięcia karty z Elvisem, syntezatorem Moog i Billie Holiday — idealne na pracę w grupach.',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/6DeTpQ8vSNr5Dz4D1KM8eW/Captura-de-pantalla-2026-09-06-a-las-12.42.00_a._m..webp',
    level: 'Powtórka · Poziom trudny',
    title: 'Krzyżówka Muzyczna',
    caption:
      'Dwadzieścia terminów muzycznych w jednej krzyżówce — świetna powtórka przed sprawdzianem.',
  },
]

export const bonuses: Bonus[] = [
  {
    title: 'Plan Lekcji w 10 Minut™',
    text: 'Szybki system przygotowania zajęć muzyki.',
    detail:
      'Dowiedz się, dlaczego 10 minut wystarczy dzięki zasadzie minimalizmu w edukacji muzycznej.',
    was: '60 zł',
    cover:
      'https://cdn.magicpatterns.com/patterns/generated-images/baef137b-3b95-495a-b420-8c9c001d1154.jpg',
  },
  {
    title: 'Bank Poleceń i Skryptów Nauczyciela™',
    text: 'Gotowe zdania, które budują autorytet i jasność na lekcji muzyki.',
    detail: 'Psychologia komunikacji z uczniem i moc słowa w klasie.',
    was: '40 zł',
    cover:
      'https://cdn.magicpatterns.com/patterns/generated-images/78eb1042-054f-4ec9-88eb-f4898dfb527c.jpg',
  },
  {
    title: 'Tracker Postępów Muzycznych™',
    text: 'Wizualny system monitorowania rozwoju i kompetencji ucznia.',
    detail:
      'Filozofia „Małych Zwycięstw” przeniesiona na grunt lekcji muzyki.',
    was: '32 zł',
    cover:
      'https://cdn.magicpatterns.com/patterns/generated-images/d1dc5976-d9ff-403e-bfde-fd8941b3ec49.jpg',
  },
  {
    title: 'Lekcje Ratunkowe 15 Minut™',
    text: 'Błyskawiczne aktywności muzyczne, które uratują Twoją lekcję w każdej sytuacji.',
    detail: 'Niezbędny „Plan B” każdego nauczyciela.',
    was: '68 zł',
    cover:
      'https://cdn.magicpatterns.com/patterns/generated-images/83e40233-9aec-43e4-a6e4-9762d0b4eb4a.jpg',
  },
  {
    title: 'Pakiet Komunikacji z Rodzicami i Dyrekcją™',
    text: 'Gotowe szablony i strategie budowania prestiżu edukacji muzycznej w Twojej szkole.',
    detail: 'Twoje drugie najważniejsze narzędzie pracy.',
    was: '68 zł',
    cover:
      'https://cdn.magicpatterns.com/patterns/generated-images/e6451e50-6cf1-41f4-9c01-723501a5b55f.jpg',
  },
]

export const offerRows: Array<{ label: string; value: string }> = [
  { label: '10 Kompletnych Tomów PDF', value: '399 zł' },
  { label: '5 Bonusów Ekskluzywnych', value: '268 zł' },
]

export const faqs: Faq[] = [
  {
    q: 'Jak otrzymam materiały?',
    a: 'Natychmiast po wpłacie otrzymasz e-mail z linkiem do wszystkich plików PDF.',
  },
  {
    q: 'Czy to jest subskrypcja?',
    a: 'Nie. Płacisz raz i masz dostęp do materiałów na zawsze.',
  },
]
